/**
 * The app's icon set: every icon the portal draws, by name. Candy draws
 * Cleanlist's drawing for it in colour (app-icon/candy-art.data.ts), Plain its
 * Lucide icon in the text colour (app-icon/lucide-icons.ts). Render with
 * <AppIcon icon={iconUser} /> from @/components/ui/app-icon.
 *
 * Names follow the Font Awesome icons they replaced, so `iconXmark` is the
 * close icon. `...Solid` is the filled or bold look for an active, selected
 * or filled state.
 */
import { defineIcon } from '@/components/ui/app-icon/icon-definition';

export type { IconDefinition } from '@/components/ui/app-icon/icon-definition';

// Regular
export const iconAddressBook = defineIcon('address-book');
export const iconAddressCard = defineIcon('address-card');
export const iconAlignLeft = defineIcon('align-left');
export const iconAnglesUpDown = defineIcon('angles-up-down');
export const iconArrowDown = defineIcon('arrow-down');
export const iconArrowDownArrowUp = defineIcon('arrow-down-arrow-up');
export const iconArrowDownAZ = defineIcon('arrow-down-a-z');
export const iconArrowLeft = defineIcon('arrow-left');
export const iconArrowLeftFromLine = defineIcon('arrow-left-from-line');
export const iconArrowRight = defineIcon('arrow-right');
export const iconArrowRightFromLine = defineIcon('arrow-right-from-line');
export const iconArrowRotateLeft = defineIcon('arrow-rotate-left');
export const iconArrowRotateRight = defineIcon('arrow-rotate-right');
export const iconArrowsLeftRight = defineIcon('arrows-left-right');
export const iconArrowsRotate = defineIcon('arrows-rotate');
export const iconArrowTrendUp = defineIcon('arrow-trend-up');
export const iconArrowUp = defineIcon('arrow-up');
export const iconArrowUpRight = defineIcon('arrow-up-right');
export const iconArrowUpRightFromSquare = defineIcon(
  'arrow-up-right-from-square'
);
export const iconArrowUpZA = defineIcon('arrow-up-z-a');
export const iconAt = defineIcon('at');
export const iconBadgeCheck = defineIcon('badge-check');
export const iconBagShopping = defineIcon('bag-shopping');
export const iconBan = defineIcon('ban');
export const iconBarsFilter = defineIcon('bars-filter');
export const iconBed = defineIcon('bed');
export const iconBell = defineIcon('bell');
export const iconBellSlash = defineIcon('bell-slash');
export const iconBinary = defineIcon('binary');
export const iconBold = defineIcon('bold');
export const iconBolt = defineIcon('bolt');
export const iconBook = defineIcon('book');
export const iconBookmark = defineIcon('bookmark');
export const iconBookOpenLines = defineIcon('book-open-lines');
export const iconBox = defineIcon('box');
export const iconBoxArchive = defineIcon('box-archive');
export const iconBoxIsometric = defineIcon('box-isometric');
export const iconBoxOpen = defineIcon('box-open');
export const iconBracketsCurly = defineIcon('brackets-curly');
export const iconBrain = defineIcon('brain');
export const iconBriefcase = defineIcon('briefcase');
export const iconBrowser = defineIcon('browser');
export const iconBuilding = defineIcon('building');
export const iconBuildingColumns = defineIcon('building-columns');
export const iconBuildings = defineIcon('buildings');
export const iconBullseye = defineIcon('bullseye');
export const iconBullseyeArrow = defineIcon('bullseye-arrow');
export const iconCalendar = defineIcon('calendar');
export const iconCalendarCheck = defineIcon('calendar-check');
export const iconCalendarClock = defineIcon('calendar-clock');
export const iconCalendarDay = defineIcon('calendar-day');
export const iconCalendarDays = defineIcon('calendar-days');
export const iconChartBar = defineIcon('chart-bar');
export const iconChartLine = defineIcon('chart-line');
export const iconChartLineUp = defineIcon('chart-line-up');
export const iconChartSimple = defineIcon('chart-simple');
export const iconChartUser = defineIcon('chart-user');
export const iconCheck = defineIcon('check');
export const iconChessRook = defineIcon('chess-rook');
export const iconChevronDown = defineIcon('chevron-down');
export const iconChevronLeft = defineIcon('chevron-left');
export const iconChevronRight = defineIcon('chevron-right');
export const iconChevronsLeft = defineIcon('chevrons-left');
export const iconChevronsRight = defineIcon('chevrons-right');
export const iconChevronUp = defineIcon('chevron-up');
export const iconChurch = defineIcon('church');
export const iconCircle = defineIcon('circle');
export const iconCircleArrowUp = defineIcon('circle-arrow-up');
export const iconCircleCheck = defineIcon('circle-check');
export const iconCircleChevronDown = defineIcon('circle-chevron-down');
export const iconCircleExclamation = defineIcon('circle-exclamation');
export const iconCircleInfo = defineIcon('circle-info');
export const iconCircleQuestion = defineIcon('circle-question');
export const iconCircleXmark = defineIcon('circle-xmark');
export const iconClipboardList = defineIcon('clipboard-list');
export const iconClock = defineIcon('clock');
export const iconClockRotateLeft = defineIcon('clock-rotate-left');
export const iconCloudArrowUp = defineIcon('cloud-arrow-up');
export const iconCode = defineIcon('code');
export const iconCodeBranch = defineIcon('code-branch');
export const iconCoin = defineIcon('coin');
export const iconCoins = defineIcon('coins');
export const iconCommand = defineIcon('command');
export const iconCopy = defineIcon('copy');
export const iconCreditCard = defineIcon('credit-card');
export const iconCube = defineIcon('cube');
export const iconDatabase = defineIcon('database');
export const iconDesktop = defineIcon('desktop');
export const iconDollarSign = defineIcon('dollar-sign');
export const iconDownLeftAndUpRightToCenter = defineIcon(
  'down-left-and-up-right-to-center'
);
export const iconDownload = defineIcon('download');
export const iconDownToLine = defineIcon('down-to-line');
export const iconDroplet = defineIcon('droplet');
export const iconEllipsis = defineIcon('ellipsis');
export const iconEllipsisVertical = defineIcon('ellipsis-vertical');
export const iconEnvelope = defineIcon('envelope');
export const iconEnvelopeCircleCheck = defineIcon('envelope-circle-check');
export const iconEnvelopeOpen = defineIcon('envelope-open');
export const iconExpand = defineIcon('expand');
export const iconEye = defineIcon('eye');
export const iconEyeSlash = defineIcon('eye-slash');
export const iconFile = defineIcon('file');
export const iconFileArrowUp = defineIcon('file-arrow-up');
export const iconFileCsv = defineIcon('file-csv');
export const iconFileExport = defineIcon('file-export');
export const iconFileLines = defineIcon('file-lines');
export const iconFileMagnifyingGlass = defineIcon('file-magnifying-glass');
export const iconFileSpreadsheet = defineIcon('file-spreadsheet');
export const iconFilter = defineIcon('filter');
export const iconFingerprint = defineIcon('fingerprint');
export const iconFlask = defineIcon('flask');
export const iconFloppyDisk = defineIcon('floppy-disk');
export const iconFolder = defineIcon('folder');
export const iconFolderOpen = defineIcon('folder-open');
export const iconFolderPlus = defineIcon('folder-plus');
export const iconGauge = defineIcon('gauge');
export const iconGear = defineIcon('gear');
export const iconGem = defineIcon('gem');
export const iconGift = defineIcon('gift');
export const iconGlobe = defineIcon('globe');
export const iconGrid2 = defineIcon('grid-2');
export const iconGripVertical = defineIcon('grip-vertical');
export const iconHand = defineIcon('hand');
export const iconHandshake = defineIcon('handshake');
export const iconHashtag = defineIcon('hashtag');
export const iconHeading = defineIcon('heading');
export const iconHeadset = defineIcon('headset');
export const iconHospital = defineIcon('hospital');
export const iconHouse = defineIcon('house');
export const iconHouseUser = defineIcon('house-user');
export const iconIdCard = defineIcon('id-card');
export const iconImage = defineIcon('image');
export const iconInbox = defineIcon('inbox');
export const iconIndustry = defineIcon('industry');
export const iconItalic = defineIcon('italic');
export const iconKey = defineIcon('key');
export const iconKeyboard = defineIcon('keyboard');
export const iconLanguage = defineIcon('language');
export const iconLayerGroup = defineIcon('layer-group');
export const iconLifeRing = defineIcon('life-ring');
export const iconLightbulb = defineIcon('lightbulb');
export const iconLightbulbSlash = defineIcon('lightbulb-slash');
export const iconLink = defineIcon('link');
export const iconList = defineIcon('list');
export const iconListBullets = defineIcon('list-bullets');
export const iconListCheck = defineIcon('list-check');
export const iconListOl = defineIcon('list-ol');
export const iconListUl = defineIcon('list-ul');
export const iconLocationDot = defineIcon('location-dot');
export const iconLock = defineIcon('lock');
export const iconLockKeyhole = defineIcon('lock-keyhole');
export const iconMagnifyingGlass = defineIcon('magnifying-glass');
export const iconMagnifyingGlassChart = defineIcon('magnifying-glass-chart');
export const iconMagnifyingGlassMinus = defineIcon('magnifying-glass-minus');
export const iconMailbox = defineIcon('mailbox');
export const iconMedal = defineIcon('medal');
export const iconMessage = defineIcon('message');
export const iconMessageDots = defineIcon('message-dots');
export const iconMinus = defineIcon('minus');
export const iconMobileScreenButton = defineIcon('mobile-screen-button');
export const iconMoon = defineIcon('moon');
export const iconPalette = defineIcon('palette');
export const iconPaperclip = defineIcon('paperclip');
export const iconPaperPlane = defineIcon('paper-plane');
export const iconPaperPlaneTop = defineIcon('paper-plane-top');
/** Sequences in the sidebar: the plane with Clu (Plain); Candy's plane. */
export const iconPaperPlaneClu = defineIcon('paper-plane-clu');
export const iconPartyHorn = defineIcon('party-horn');
export const iconPause = defineIcon('pause');
export const iconPen = defineIcon('pen');
export const iconPencil = defineIcon('pencil');
export const iconPhone = defineIcon('phone');
export const iconPhoneHangup = defineIcon('phone-hangup');
export const iconMicrophone = defineIcon('microphone');
export const iconMicrophoneSlash = defineIcon('microphone-slash');
export const iconDialpad = defineIcon('dialpad');
export const iconPhoneVolume = defineIcon('phone-volume');
export const iconPlay = defineIcon('play');
export const iconPlug = defineIcon('plug');
export const iconPlugCircleXmark = defineIcon('plug-circle-xmark');
export const iconPlus = defineIcon('plus');
export const iconPresentationScreen = defineIcon('presentation-screen');
export const iconPuzzlePiece = defineIcon('puzzle-piece');
export const iconQuestion = defineIcon('question');
export const iconQuoteLeft = defineIcon('quote-left');
export const iconReceipt = defineIcon('receipt');
export const iconRectangleList = defineIcon('rectangle-list');
export const iconReply = defineIcon('reply');
export const iconRightFromBracket = defineIcon('right-from-bracket');
export const iconRobot = defineIcon('robot', { mark: true });
export const iconRocket = defineIcon('rocket');
export const iconRotate = defineIcon('rotate');
export const iconSackDollar = defineIcon('sack-dollar');
export const iconSchool = defineIcon('school');
export const iconSeedling = defineIcon('seedling');
export const iconShareNodes = defineIcon('share-nodes');
export const iconShieldCheck = defineIcon('shield-check');
export const iconShieldExclamation = defineIcon('shield-exclamation');
export const iconShieldHalved = defineIcon('shield-halved');
export const iconSidebar = defineIcon('sidebar');
export const iconSidebarFlip = defineIcon('sidebar-flip');
export const iconSignalStream = defineIcon('signal-stream');
export const iconSignature = defineIcon('signature');
export const iconSitemap = defineIcon('sitemap');
export const iconSliders = defineIcon('sliders');
export const iconSparkles = defineIcon('sparkles');
export const iconSpinnerThird = defineIcon('spinner-third');
export const iconSquare = defineIcon('square');
export const iconSquareCheck = defineIcon('square-check');
export const iconStar = defineIcon('star');
export const iconStop = defineIcon('stop');
export const iconStopwatch = defineIcon('stopwatch');
export const iconStore = defineIcon('store');
export const iconSun = defineIcon('sun');
export const iconT = defineIcon('t');
export const iconTable = defineIcon('table');
export const iconTableColumns = defineIcon('table-columns');
export const iconTag = defineIcon('tag');
export const iconText = defineIcon('text');
export const iconThumbsDown = defineIcon('thumbs-down');
export const iconThumbsUp = defineIcon('thumbs-up');
export const iconThumbtack = defineIcon('thumbtack');
export const iconThumbtackSlash = defineIcon('thumbtack-slash');
export const iconTicket = defineIcon('ticket');
export const iconToggleOn = defineIcon('toggle-on');
export const iconTrash = defineIcon('trash');
export const iconTriangleExclamation = defineIcon('triangle-exclamation');
export const iconUpload = defineIcon('upload');
export const iconUpRightAndDownLeftFromCenter = defineIcon(
  'up-right-and-down-left-from-center'
);
export const iconUser = defineIcon('user');
export const iconUserCheck = defineIcon('user-check');
export const iconUserGear = defineIcon('user-gear');
export const iconUserGroup = defineIcon('user-group');
export const iconUserMagnifyingGlass = defineIcon('user-magnifying-glass');
export const iconUserMinus = defineIcon('user-minus');
export const iconUserPlus = defineIcon('user-plus');
export const iconUsers = defineIcon('users');
export const iconUserSlash = defineIcon('user-slash');
export const iconWallet = defineIcon('wallet');
export const iconWandMagicSparkles = defineIcon('wand-magic-sparkles');
export const iconWarehouse = defineIcon('warehouse');
export const iconWavePulse = defineIcon('wave-pulse');
export const iconWebhook = defineIcon('webhook');
export const iconWindowRestore = defineIcon('window-restore');
export const iconXmark = defineIcon('xmark');

// Solid: the filled or bold look for active, selected and filled states
export const iconAddressBookSolid = defineIcon('address-book', { solid: true });
export const iconArrowLeftFromLineSolid = defineIcon('arrow-left-from-line', {
  solid: true,
});
export const iconArrowRightFromLineSolid = defineIcon('arrow-right-from-line', {
  solid: true,
});
export const iconArrowUpRightFromSquareSolid = defineIcon(
  'arrow-up-right-from-square',
  { solid: true }
);
export const iconBadgeCheckSolid = defineIcon('badge-check', { solid: true });
export const iconBedSolid = defineIcon('bed', { solid: true });
export const iconBellSolid = defineIcon('bell', { solid: true });
export const iconBoltSolid = defineIcon('bolt', { solid: true });
export const iconBookmarkSolid = defineIcon('bookmark', { solid: true });
export const iconBookOpenLinesSolid = defineIcon('book-open-lines', {
  solid: true,
});
export const iconBookSolid = defineIcon('book', { solid: true });
export const iconBriefcaseSolid = defineIcon('briefcase', { solid: true });
export const iconBrowserSolid = defineIcon('browser', { solid: true });
export const iconBuildingSolid = defineIcon('building', { solid: true });
export const iconBuildingsSolid = defineIcon('buildings', { solid: true });
export const iconCalendarSolid = defineIcon('calendar', { solid: true });
export const iconChartLineSolid = defineIcon('chart-line', { solid: true });
export const iconChartLineUpSolid = defineIcon('chart-line-up', {
  solid: true,
});
export const iconChartUserSolid = defineIcon('chart-user', { solid: true });
export const iconCheckSolid = defineIcon('check', { solid: true });
export const iconChevronDownSolid = defineIcon('chevron-down', { solid: true });
export const iconChevronRightSolid = defineIcon('chevron-right', {
  solid: true,
});
export const iconCircleArrowUpSolid = defineIcon('circle-arrow-up', {
  solid: true,
});
export const iconCircleCheckSolid = defineIcon('circle-check', { solid: true });
export const iconCircleChevronDownSolid = defineIcon('circle-chevron-down', {
  solid: true,
});
export const iconCircleExclamationSolid = defineIcon('circle-exclamation', {
  solid: true,
});
export const iconCircleInfoSolid = defineIcon('circle-info', { solid: true });
export const iconCircleQuestionSolid = defineIcon('circle-question', {
  solid: true,
});
export const iconCircleSolid = defineIcon('circle', { solid: true });
export const iconCircleXmarkSolid = defineIcon('circle-xmark', { solid: true });
export const iconClipboardListSolid = defineIcon('clipboard-list', {
  solid: true,
});
export const iconClockSolid = defineIcon('clock', { solid: true });
export const iconCodeSolid = defineIcon('code', { solid: true });
export const iconCoinSolid = defineIcon('coin', { solid: true });
export const iconCoinsSolid = defineIcon('coins', { solid: true });
export const iconCopySolid = defineIcon('copy', { solid: true });
export const iconCreditCardSolid = defineIcon('credit-card', { solid: true });
export const iconDatabaseSolid = defineIcon('database', { solid: true });
export const iconDesktopSolid = defineIcon('desktop', { solid: true });
export const iconDownloadSolid = defineIcon('download', { solid: true });
export const iconEllipsisSolid = defineIcon('ellipsis', { solid: true });
export const iconEllipsisVerticalSolid = defineIcon('ellipsis-vertical', {
  solid: true,
});
export const iconEnvelopeOpenSolid = defineIcon('envelope-open', {
  solid: true,
});
export const iconEnvelopeSolid = defineIcon('envelope', { solid: true });
export const iconEyeSolid = defineIcon('eye', { solid: true });
export const iconFileArrowUpSolid = defineIcon('file-arrow-up', {
  solid: true,
});
export const iconFileLinesSolid = defineIcon('file-lines', { solid: true });
export const iconFileSolid = defineIcon('file', { solid: true });
export const iconFilterSolid = defineIcon('filter', { solid: true });
export const iconFloppyDiskSolid = defineIcon('floppy-disk', { solid: true });
export const iconFolderOpenSolid = defineIcon('folder-open', { solid: true });
export const iconFolderSolid = defineIcon('folder', { solid: true });
export const iconGearSolid = defineIcon('gear', { solid: true });
export const iconGemSolid = defineIcon('gem', { solid: true });
export const iconGiftSolid = defineIcon('gift', { solid: true });
export const iconGlobeSolid = defineIcon('globe', { solid: true });
export const iconGrid2Solid = defineIcon('grid-2', { solid: true });
export const iconHouseSolid = defineIcon('house', { solid: true });
export const iconIdCardSolid = defineIcon('id-card', { solid: true });
export const iconInboxSolid = defineIcon('inbox', { solid: true });
export const iconKeyboardSolid = defineIcon('keyboard', { solid: true });
export const iconKeySolid = defineIcon('key', { solid: true });
export const iconLayerGroupSolid = defineIcon('layer-group', { solid: true });
export const iconLightbulbSolid = defineIcon('lightbulb', { solid: true });
export const iconLinkSolid = defineIcon('link', { solid: true });
export const iconListCheckSolid = defineIcon('list-check', { solid: true });
export const iconListSolid = defineIcon('list', { solid: true });
export const iconListUlSolid = defineIcon('list-ul', { solid: true });
export const iconLocationDotSolid = defineIcon('location-dot', { solid: true });
export const iconLockKeyholeSolid = defineIcon('lock-keyhole', { solid: true });
export const iconLockSolid = defineIcon('lock', { solid: true });
export const iconMagnifyingGlassChartSolid = defineIcon(
  'magnifying-glass-chart',
  { solid: true }
);
export const iconMagnifyingGlassSolid = defineIcon('magnifying-glass', {
  solid: true,
});
export const iconMessageDotsSolid = defineIcon('message-dots', { solid: true });
export const iconMessageSolid = defineIcon('message', { solid: true });
export const iconMobileScreenButtonSolid = defineIcon('mobile-screen-button', {
  solid: true,
});
export const iconMoonSolid = defineIcon('moon', { solid: true });
export const iconPaperPlaneTopSolid = defineIcon('paper-plane-top', {
  solid: true,
});
export const iconPaperPlaneCluSolid = defineIcon('paper-plane-clu', {
  solid: true,
});
export const iconPartyHornSolid = defineIcon('party-horn', { solid: true });
export const iconPauseSolid = defineIcon('pause', { solid: true });
export const iconPencilSolid = defineIcon('pencil', { solid: true });
export const iconPhoneSolid = defineIcon('phone', { solid: true });
export const iconPhoneVolumeSolid = defineIcon('phone-volume', { solid: true });
export const iconPlaySolid = defineIcon('play', { solid: true });
export const iconPlusSolid = defineIcon('plus', { solid: true });
export const iconPresentationScreenSolid = defineIcon('presentation-screen', {
  solid: true,
});
export const iconPuzzlePieceSolid = defineIcon('puzzle-piece', { solid: true });
export const iconQuoteLeftSolid = defineIcon('quote-left', { solid: true });
export const iconRightFromBracketSolid = defineIcon('right-from-bracket', {
  solid: true,
});
export const iconRobotSolid = defineIcon('robot', { solid: true, mark: true });
export const iconRocketLaunchSolid = defineIcon('rocket-launch', {
  solid: true,
});
export const iconSchoolSolid = defineIcon('school', { solid: true });
export const iconShieldCheckSolid = defineIcon('shield-check', { solid: true });
export const iconSidebarSolid = defineIcon('sidebar', { solid: true });
export const iconSitemapSolid = defineIcon('sitemap', { solid: true });
export const iconSlidersSolid = defineIcon('sliders', { solid: true });
export const iconSparklesSolid = defineIcon('sparkles', { solid: true });
export const iconSquareCheckSolid = defineIcon('square-check', { solid: true });
export const iconSquareSolid = defineIcon('square', { solid: true });
export const iconStarSolid = defineIcon('star-solid', { solid: true });
export const iconStarsSolid = defineIcon('stars', { solid: true });
export const iconStopSolid = defineIcon('stop', { solid: true });
export const iconSunSolid = defineIcon('sun', { solid: true });
export const iconTableSolid = defineIcon('table', { solid: true });
export const iconTagSolid = defineIcon('tag', { solid: true });
export const iconThumbsDownSolid = defineIcon('thumbs-down', { solid: true });
export const iconThumbsUpSolid = defineIcon('thumbs-up', { solid: true });
export const iconThumbtackSolid = defineIcon('thumbtack', { solid: true });
export const iconTicketSolid = defineIcon('ticket', { solid: true });
export const iconToggleOnSolid = defineIcon('toggle-on', { solid: true });
export const iconTrashSolid = defineIcon('trash', { solid: true });
export const iconTriangleExclamationSolid = defineIcon('triangle-exclamation', {
  solid: true,
});
export const iconUserMagnifyingGlassSolid = defineIcon(
  'user-magnifying-glass',
  { solid: true }
);
export const iconUserSolid = defineIcon('user', { solid: true });
export const iconUsersSolid = defineIcon('users', { solid: true });
export const iconWindowRestoreSolid = defineIcon('window-restore', {
  solid: true,
});
export const iconXmarkSolid = defineIcon('xmark', { solid: true });

// Brands
export const iconAttio = defineIcon('attio', { logo: '/logos/attio.svg' });
export const iconChrome = defineIcon('chrome', {
  logo: '/logos/chrome-web-store-mark.png',
});
// The Cleanlist mark, the Chrome extension's own icon.
export const iconCleanlist = defineIcon('cleanlist', {
  logo: '/cleanlist-icon-black.svg',
  tinted: true,
});
export const iconHubspot = defineIcon('hubspot', {
  logo: '/logos/hubspot.svg',
});
export const iconLinkedin = defineIcon('linkedin');
export const iconSalesforce = defineIcon('salesforce', {
  logo: '/logos/salesforce.svg',
});
export const iconSlack = defineIcon('slack');
export const iconTeams = defineIcon('teams');
export const iconTrellus = defineIcon('trellus', {
  logo: '/logos/trellus.png',
});
export const iconXTwitter = defineIcon('x-twitter');

// Beyond the Font Awesome set: icons that were hand-drawn inline before
export const iconWifiSlash = defineIcon('wifi-slash');

// Find People and Find Companies filter rows (search overhaul)
export const iconHeadlineCard = defineIcon('headline-card');
export const iconSummaryDoc = defineIcon('summary-doc');
export const iconXHandle = defineIcon('x-handle');
export const iconHourglass = defineIcon('hourglass');
export const iconStudyBook = defineIcon('study-book');
export const iconConnections = defineIcon('connections');
export const iconCompanyLinkedin = defineIcon('company-linkedin');
export const iconNaicsCode = defineIcon('naics-code');
export const iconStockCandles = defineIcon('stock-candles');
export const iconPastCompany = defineIcon('past-company');
export const iconPastTitle = defineIcon('past-title');
export const iconPastSeniority = defineIcon('past-seniority');
export const iconPastDepartment = defineIcon('past-department');
export const iconHqRegion = defineIcon('hq-region');
export const iconTitleBan = defineIcon('title-ban');
export const iconCompanyBan = defineIcon('company-ban');
export const iconHeadcountGrowth = defineIcon('headcount-growth');
export const iconBoxCheck = defineIcon('box-check');
export const iconBoxMinus = defineIcon('box-minus');
export const iconSalesforceCloud = defineIcon('salesforce-cloud');
export const iconHubspotSprocket = defineIcon('hubspot-sprocket');
export const iconSparkPlain = defineIcon('spark-plain');
export const iconTeamSize = defineIcon('team-size');
export const iconInvestor = defineIcon('investor');
export const iconCity = defineIcon('city');
export const iconStateProvince = defineIcon('state-province');
export const iconHqLocation = defineIcon('hq-location');
export const iconHqCity = defineIcon('hq-city');
export const iconRevenue = defineIcon('revenue');
export const iconJobChange = defineIcon('job-change');
export const iconProfileRefreshed = defineIcon('profile-refreshed');
export const iconTeamMix = defineIcon('team-mix');
